import React from 'react';
import { Title, Loader, ReloadIconButton, ClearFilters, TablePaginationWrapper } from '../../elements';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import {
  Grid,
  Paper,
  TableRow,
  TableBody,
  TableCell,
  Table,
  TableHead,
  Tooltip,
  IconButton,
  Icon,
  Button,
  TextField,
  Link,
  Chip
} from '@material-ui/core';
import { useStyles } from './styles';
import { notifications, extractApiErrorMessage } from '../../utils';
import { useTranslation } from 'react-i18next';
import VetService from '../../hooks/VetService';
import { VeterinaryViewModel } from '../../models/vets';
import { CreateUpdateVeterinaryModal } from '../../components';
import { useNavigate } from 'react-router-dom';

const VetsPage: React.FC<any> = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const vetService = new VetService();
  const { t } = useTranslation();

  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [createUpdateVetDialogOpen, setCreateUpdateVetDialogOpen] = React.useState(false);
  const [vetMode, setVetMode] = React.useState('create' as 'create' | 'update');
  const [selectedVet, setSelectedVet] = React.useState(null as VeterinaryViewModel | null);

  const [vets, setVets] = React.useState([] as VeterinaryViewModel[]);

  const [searchText, setSearchText] = React.useState('');

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };
  // * pagination

  const loadVets = async () => {
    setIsBusy(true);

    const searchPage = page === 0 ? 1 : page + 1;

    vetService
      .getVeterinaries(searchPage, rowsPerPage, searchText)
      .then((result) => {
        setVets(result.data);
        setTotal(result.total);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (!loaded) {
      loadVets();
    }
  }, [loaded]);

  const handleCreateUpdateVet = (result: boolean | null) => {
    setCreateUpdateVetDialogOpen(false);

    if (result) {
      setLoaded(false);
    }
  };

  const editVet = (vet: VeterinaryViewModel) => {
    setSelectedVet(vet);
    setVetMode('update');
    setCreateUpdateVetDialogOpen(true);
  };

  const createVet = () => {
    setSelectedVet(null);
    setVetMode('create');
    setCreateUpdateVetDialogOpen(true);
  };

  const viewVeterinary = (id: string) => {
    navigate(`/veterinary/${id}`);
  };

  const renderPhoneCell = (vet: VeterinaryViewModel) => {
    if (!vet.settings || !vet.settings.phone) return '-';
    return (
      <Link href={`tel:${vet.settings.phone}`} variant="body2">
        {vet.settings.phone}
      </Link>
    );
  };

  const renderAddressCell = (vet: VeterinaryViewModel) => {
    if (!vet.settings) return '-';
    const address = [vet.settings.country, vet.settings.address];
    return address.join(', ');
  };

  const renderActionCell = (vet: VeterinaryViewModel) => {
    return (
      <>
        <Tooltip arrow title={t('f-vets-upd')}>
          <IconButton id={`upd-${vet.id}`} size="small" color="primary" onClick={() => editVet(vet)}>
            <Icon>{'edit'}</Icon>
          </IconButton>
        </Tooltip>

        <Tooltip arrow title={t('f-vets-view')}>
          <IconButton id={`vet-view-${vet.id}`} size="small" color="primary" onClick={() => viewVeterinary(vet.id)}>
            <Icon>{'visibility'}</Icon>
          </IconButton>
        </Tooltip>
      </>
    );
  };

  const clearFilters = () => {
    setSearchText('');
  };

  const renderFiltersSection = () => {
    return (
      <Grid container spacing={1} className={classes.centred}>
        <Grid item>
          <TextField
            label={t('f-com-search-farm')}
            variant="outlined"
            size="small"
            fullWidth
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setLoaded(false);
              }
            }}
            onKeyUp={(e) => {
              if (e.key === 'Backspace') {
                setLoaded(false);
              }
            }}
          />
        </Grid>

        <Grid item>
          <ClearFilters onClick={clearFilters} />
        </Grid>

        <Grid item>
          <Button
            id="searchBtn"
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setLoaded(false)}
            startIcon={<Icon>{'search'}</Icon>}
          >
            {t('f-search')}
          </Button>
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          {/* title & spinner */}

          <Grid container direction="row" spacing={1} className={classes.paperHeader}>
            <Grid item container md={6} xs={10} spacing={1}>
              <Grid item>
                <Icon fontSize="large" color="primary">
                  {'local_pharmacy'}
                </Icon>
              </Grid>
              <Grid item>
                <Title>{t('f-vets')}</Title>
              </Grid>
              <Grid item>
                <Chip label={total} variant="outlined" />
              </Grid>
            </Grid>

            <Grid item container className={classes.justifyRight} md={6} xs={10}>
              <Grid item>
                <ReloadIconButton action={() => setLoaded(false)} />
              </Grid>

              <Grid item>
                <Button
                  id="createVetBtn"
                  className={classes.withMarginLeft}
                  size="small"
                  color="default"
                  variant="contained"
                  startIcon={<Icon>{'add_circle_outline'}</Icon>}
                  onClick={createVet}
                >
                  {t('f-vets-add')}
                </Button>
              </Grid>
            </Grid>
          </Grid>

          {renderFiltersSection()}

          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {/* data table */}
          {loaded && (
            <Grid item md={12} xs={12}>
              <TablePaginationWrapper
                rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              >
                <Paper elevation={1}>
                  <Table size="small" id="vetsResultTable">
                    <TableHead className={classes.tableHeader}>
                      <TableRow>
                        <TableCell>{t('f-com-number')}</TableCell>
                        <TableCell>{t('f-com-name')}</TableCell>
                        <TableCell>{t('f-com-created')}</TableCell>
                        <TableCell>{t('f-com-address')}</TableCell>
                        <TableCell>{t('f-com-phone')}</TableCell>
                        <TableCell>{t('f-com-contacts')}</TableCell>
                        <TableCell>{''}</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {vets.map((vet) => (
                        <TableRow key={vet.id} hover>
                          <TableCell variant="head">{vet.number}</TableCell>
                          <TableCell className={`${vet.isSoftDeleted ? classes.vertNameDeleted : classes.vertName}`}>
                            {vet.name}
                          </TableCell>
                          <TableCell>{new Date(vet.created).toLocaleString()}</TableCell>
                          <TableCell>{renderAddressCell(vet)}</TableCell>
                          <TableCell>{renderPhoneCell(vet)}</TableCell>
                          <TableCell>{vet.contacts.length}</TableCell>
                          <TableCell>{renderActionCell(vet)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </TablePaginationWrapper>
            </Grid>
          )}
        </Grid>
      </Grid>

      <CreateUpdateVeterinaryModal
        opened={createUpdateVetDialogOpen}
        onModalAction={handleCreateUpdateVet}
        mode={vetMode}
        vet={selectedVet}
      />
    </>
  );
};

export default VetsPage;
